import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = controleren, false = anoniem
  const [error, setError] = useState("");

  // De sessie zit in een httpOnly-cookie: alleen de server weet of we ingelogd zijn.
  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      if (e?.response?.status !== 401) {
        console.error("Sessiecontrole mislukt:", apiError(e));
      }
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser({ ...data.user, access: data.access });
      return true;
    } catch (e) {
      setError(apiError(e));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Uitloggen op de server mislukte, sessie lokaal beëindigd:", apiError(e));
    }
    setUser(false);
  }, []);

  const startImpersonation = useCallback(async (accountId) => {
    const { data } = await api.post("/admin/impersonate", { account_id: accountId });
    await check();
    return data;
  }, [check]);

  const stopImpersonation = useCallback(async () => {
    await api.post("/admin/impersonate/stop");
    await check();
  }, [check]);

  const value = useMemo(
    () => ({
      user,
      account: user?.account || null,
      isMaster: user?.platform_role === "MASTER_ADMIN",
      impersonation: user?.impersonation || { active: false },
      accessAllowed: user ? user?.access?.allowed !== false : true,
      accessReason: user?.access?.reason || "",
      login,
      logout,
      error,
      setError,
      refresh: check,
      startImpersonation,
      stopImpersonation,
    }),
    [user, error, check, login, logout, startImpersonation, stopImpersonation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
